import clsx from 'clsx'
import { useRef, useState } from 'react'

import {
  baseClassNames,
  errorClassNames,
  attentionClassNames,
  widths,
  heights,
  texts,
  rounds,
} from '../const'
import style from './style.module.css'
import { InputNumberProps } from './type'
import { formatNumber } from './util'

/**
 * 数量（正の整数）を入力するコンポーネント
 */
export const InputNumber = ({
  id,
  name,
  value,
  placeholder,
  disabled = false,
  min = 0,
  max,
  height = 'lg',
  rounded = 'all',
  isError = false,
  selectOnFocus = true,
  zeroAttention = false,
  tabIndex,
  enterIndex,
  onChange,
  onBlur,
  onFocus = () => {},
}: InputNumberProps) => {
  const isValueZero = value === 0
  const [isComposing, setIsComposing] = useState(false)
  const isCompositionEndRecently = useRef(false)

  const IME_TYPES = [
    'insertCompositionText',
    'deleteCompositionText',
    'insertFromComposition',
    'deleteByComposition',
  ]

  const handleChange = (value: string) => {
    const num = formatNumber(value, min, max)
    if (num !== null && onChange) {
      onChange(num)
    }
  }

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    /**
     * Windows限定で、CompositionEnd のイベントの後すぐに Input のイベントが発生する。
     * このイベントに含まれるテキストは、IME状態でのテキストの文字数分削除されたテキストになり、
     * 予期せず入力中の文字列が削除されてしまう。
     * これを防ぐために、CompositionEnd のイベントの後すぐに発生した Input のイベントは無視する
     * https://qiita.com/marumo3/items/8ae547ab654b9711505a
     */
    if (isCompositionEndRecently.current) {
      isCompositionEndRecently.current = false
      return
    }
    if (
      e.nativeEvent instanceof InputEvent &&
      IME_TYPES.includes(e.nativeEvent.inputType)
    ) {
      return
    }

    const num = formatNumber(e.currentTarget.value, min, max)

    if (num !== null && onChange) {
      onChange(num)
    }
  }

  const handleCompositionEnd = (
    event: React.CompositionEvent<HTMLInputElement>
  ) => {
    isCompositionEndRecently.current = true
    setIsComposing(false)
    handleChange(event.currentTarget.value)
  }

  const handleCompositionStart = () => {
    setIsComposing(true)
  }

  return (
    <div>
      <input
        id={id}
        name={name}
        placeholder={placeholder}
        className={clsx(
          style.hiddenArrow,
          baseClassNames,
          isError && !disabled && errorClassNames,
          widths['full'],
          heights[height],
          texts[height],
          rounds[rounded],
          isValueZero && zeroAttention && attentionClassNames,
          disabled &&
            'tw-pointer-events-none tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-600'
        )}
        value={isComposing ? undefined : value} // WindowsのIME用にComposing中は Uncontrolled にする
        min={min}
        max={max}
        tabIndex={disabled ? -1 : tabIndex}
        type="text"
        inputMode="numeric"
        onInput={handleInput}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onBlur={(event) => {
          const num = formatNumber(event.currentTarget.value, min, max)
          if (num !== null && onBlur) {
            onBlur(num)
          }
        }}
        onFocus={(event) => {
          if (selectOnFocus) {
            event.currentTarget.select()
          }
          onFocus()
        }}
        data-enterindex={enterIndex ? (disabled ? -1 : tabIndex ?? 0) : null}
      />
    </div>
  )
}
